import { useEffect } from "react";
import type { FieldPath, UseFormReturn } from "react-hook-form";
import type { TRequest } from "@shared/entities";
import { useStore } from "src/store/store";

const PROFILE_FIELDS = [
	"firstName",
	"lastName",
	"email",
	"phone",
	"phone2",
	"IDNumber",
	"dob",
	"sex",
	"healthInsurance",
	"city",
	"street",
	"houseNumber",
	"apartmentNumber",
] as const;

/**
 * Fills a client's new-request form with their profile details once the
 * profile is loaded.
 *
 * defaultValues are read once, when the form mounts. Opened from inside the
 * app, the profile is already there; opened by loading or reloading the page
 * itself (which the automatic move to a new version does), the form mounts
 * first and the profile lands a moment later — leaving every detail empty.
 *
 * Only fields that are still empty are filled: never over what the client
 * typed, nor over a draft request being continued.
 */
export function usePrefillClientDetails(methods: UseFormReturn<TRequest>) {
	const store = useStore();
	const profile = store.profile;
	const uid = store.user?.uid;

	useEffect(() => {
		if (uid && !methods.getValues("userId")) methods.setValue("userId", uid);
		if (!profile) return;
		for (const field of PROFILE_FIELDS) {
			const path = `clientDetails.${field}` as FieldPath<TRequest>;
			const value = profile[field];
			if (value && !methods.getValues(path)) methods.setValue(path, value);
		}
	}, [profile, uid, methods]);
}
